import {
  Modal,
  Input,
  Button,
  Select,
  DatePicker,
  Empty,
  Grid,
  Tag,
  Upload,
  message,
} from "antd";

import {
  PhoneOutlined,
  MessageOutlined,
  FileTextOutlined,
  RobotOutlined,
  PaperClipOutlined,
} from "@ant-design/icons";

import {
  useEffect,
  useRef,
  useState,
} from "react";

import dayjs from "dayjs";

import {
  saveConversationApi,
  getConversationApi,
  getLeadActivitiesApi,
} from "../../../../apiservice/crm";

import { useSelector } from "react-redux";

import { selectAuthInfo }
from "../../../Auth/authSelectors";
import { convertToCompanyTimezoneObject, formatDayjsToUtc } from "../../../../lib/functions";
import AttachmentPreviewList from "../../../../components/AttachmentPreviewList";
import VoiceRecorder from "../../../../components/VoiceRecorder";
import "../../crmSharedStyle.css";

const LeadActivityModal = ({
  open,
  onClose,
  lead,
}) => {

  const [text, setText] = useState("");
  const [type, setType] = useState("note");
  const [list, setList] = useState([]);
  const [callStatus, setCallStatus] = useState(null);
  const [followUpDate, setFollowUpDate] = useState(null);
  const [fileList, setFileList] = useState([]);

  const scrollRef = useRef(null);

  const authInfo =
    useSelector(selectAuthInfo);

    const { useBreakpoint } = Grid;
    const screens = useBreakpoint();
    const isMobile = !screens.md;

  const load = async () => {
    if (!lead?.id) return;

    try {
      const res =
        // await getConversationApi({
        //   customer_id: lead.id,
        // });
        await getLeadActivitiesApi({
          lead_id: lead.id,
           currency: authInfo?.currency,
        });

      setList(res.data || []);
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    if (open) load();
  }, [open]);

  useEffect(() => {
    scrollRef.current?.scrollIntoView({
      behavior: "smooth",
    });
  }, [list]);

  const save = async () => {
    // A voice note or other attachment is a complete activity on its own --
    // typed text was previously required even when there was nothing to
    // type (e.g. a voice-only note), silently no-op-ing the button.
    if (!text.trim() && !fileList.length) return;

    const followUpUtc = followUpDate
      ? formatDayjsToUtc(followUpDate)
      : null;

    try {
      if (fileList.length) {
        const formData = new FormData();

        formData.append("customer_id", lead.id);
        formData.append("text", text);
        formData.append("type", type);

        if (callStatus) formData.append("call_status", callStatus);
        if (followUpUtc) formData.append("follow_up_date", followUpUtc);

        fileList.forEach((f) => {
          formData.append("attachments", f.originFileObj || f);
        });

        await saveConversationApi(formData);
      } else {
        await saveConversationApi({
          customer_id: lead.id,
          text,
          type,
          call_status: callStatus,
          follow_up_date: followUpUtc,
        });
      }

      setText("");
      setType("note");
      setCallStatus(null);
      setFollowUpDate(null);
      setFileList([]);

      load();

    } catch (err) {
      message.error(
        err?.response?.data?.message || "Failed to save activity"
      );
    }
  };

  const getIcon = (item) => {
  if (item.source === "deal") return "💰";

  if (item.source === "task") return "📅";

  if (item.type === "call") return <PhoneOutlined />;

  if (item.type === "whatsapp") return <MessageOutlined />;

  if (item.type === "meeting") return "👥";

  if (item.type === "system") return <RobotOutlined />;

  return <FileTextOutlined />;
};

  const getBg = (item) => {
  switch (item.source) {
    case "conversation":
      return "#f5f5f5";

    case "task":
      return "#fff7e6";

    case "deal":
      return "#f6ffed";

    case "deal_activity":
      return "#e6f4ff";

    default:
      return "#f5f5f5";
  }
};

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={isMobile ? "95%" : 650}
      className="crm-modal-responsive"
      title={`Activity — ${lead?.name}`}
    >

      {/* CHAT AREA */}

      <div
        style={{
          maxHeight: isMobile ? 320 : 450,
          overflowY: "auto",
          paddingRight: 6,
          marginBottom: 12,
        }}
      >

        {list.length ? (
          list.map((item) => {

            const isMe =
              item.created_by ===
              authInfo?.userInfo?.user_id;

            return (
              <div
                key={item.id}
                style={{
                  display: "flex",
                  justifyContent:
                    isMe
                      ? "flex-end"
                      : "flex-start",

                  marginBottom: 14,
                }}
              >

                <div
                  style={{
                    background:
                      getBg(item),
                    padding: isMobile ? 8 : 12,
                    borderRadius: isMobile ? 10 : 14,
                    border:
                    item.type === "call"
                        ? "1px solid #91caff"
                        : item.type === "whatsapp"
                        ? "1px solid #b7eb8f"
                        : "1px solid #eee",
                    maxWidth: isMobile ? "90%" : "75%",
                    boxShadow:
                      "0 2px 8px rgba(0,0,0,0.08)",
                  }}
                >

                  {/* HEADER */}

                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >

                    {getIcon(item)}

                    <b
                      style={{
                        textTransform:
                          "capitalize",
                        fontSize: isMobile ? 12 : 14,  
                      }}
                    >
                      {item.type || "note"}
                    </b>

                    <Tag color="purple">
                      {item.source.replace("_", " ")}
                  </Tag>

                  </div>

                  {/* TEXT */}

                  <div
                    className="crm-user-text"
                    style={{
                      marginTop: 8,
                      fontSize: isMobile ? 12 : 14,
                    }}
                  >
                    {item.text}
                  </div>

                  {/* ATTACHMENTS */}
                  <AttachmentPreviewList attachments={item.attachments || []} thumbSize={72} />

                  {/* call meta data */}
                  {item.type === "call" && (
                    <div
                      style={{
                        marginTop: 8,
                        display: "flex",
                        gap: 6,
                        flexWrap: "wrap",
                      }}
                    >
                      <Tag color="blue">
                        {item.call_status}
                      </Tag>

                      {item.follow_up_date && (
                        <Tag color="gold">
                          {
                          // dayjs(item.follow_up_date).format(
                          //   "DD MMM"
                          // )
                          convertToCompanyTimezoneObject(item.follow_up_date).format("DD MMM")
                          }
                        </Tag>
                      )}
                    </div>
                    )}

                  {/* TIME */}

                  <div
                    style={{
                      marginTop: 8,
                      fontSize: 11,
                      color: "#888",
                      textAlign: "right",
                    }}
                  >
                    {
                    // dayjs(item.created_date).format(
                    //   isMobile
                    //     ? "DD MMM hh:mmA"
                    //     : "DD MMM, hh:mm A"
                    // )
                    convertToCompanyTimezoneObject(item.created_at).format(
                        isMobile
                            ? "DD MMM hh:mmA"
                            : "DD MMM, hh:mm A"
                    )
                    }
                  </div>

                </div>

              </div>
            );
          })
        ) : (
          <Empty description="No activity yet" />
        )}

        <div ref={scrollRef} />

      </div>

      {/* COMPOSER */}
      <div className="crm-section-title" style={{ marginBottom: 8 }}>
        Add update
      </div>

      <Select
        value={type}
        size={isMobile ? "middle" : "large"}
        onChange={setType}
        style={{
          width: "100%",
          marginBottom: 10,
        }}
        options={[
          {
            label: "Note",
            value: "note",
          },
          {
            label: "Call",
            value: "call",
          },
          {
            label: "WhatsApp",
            value: "whatsapp",
          },
        ]}
      />

      {type === "call" && (
        <>
            <Select
            placeholder="Call Status"
            size={isMobile ? "middle" : "large"}
            value={callStatus}
            onChange={setCallStatus}
            style={{
                width: "100%",
                marginBottom: 10,
            }}
            options={[
                {
                label: "Connected",
                value: "connected",
                },
                {
                label: "Missed",
                value: "missed",
                },
                {
                label: "No Answer",
                value: "no_answer",
                },
            ]}
            />

            <DatePicker
            showTime
            style={{
                width: "100%",
                marginBottom: 10,
            }}
            placeholder="Follow-up Date"
            onChange={setFollowUpDate}
            />
        </>
        )}

      {/* TEXT */}

      <Input.TextArea
        className="crm-user-text"
        autoSize={{
          minRows: isMobile ? 2 : 3,
          maxRows: 10,
        }}
        value={text}
        onChange={(e) =>
          setText(e.target.value)
        }
        placeholder="Type activity..."
      />

      {/* ATTACHMENTS PICKER */}

      <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap" }}>
        <Upload
          multiple
          accept=".png,.jpg,.jpeg,.pdf"
          fileList={fileList}
          beforeUpload={(file) => {
            const isAllowed =
              ["image/png", "image/jpeg", "application/pdf"].includes(
                file.type
              );

            if (!isAllowed) {
              message.error("Only PNG, JPEG or PDF files are allowed");
              return Upload.LIST_IGNORE;
            }

            if (fileList.length >= 5) {
              message.error("Up to 5 attachments per activity");
              return Upload.LIST_IGNORE;
            }

            return false;
          }}
          onChange={({ fileList: next }) => setFileList(next)}
          onRemove={(file) =>
            setFileList((prev) => prev.filter((f) => f.uid !== file.uid))
          }
          style={{ marginTop: 8 }}
        >
          <Button icon={<PaperClipOutlined />} size="small">
            Attach file
          </Button>
        </Upload>

        <VoiceRecorder
          disabled={fileList.length >= 5}
          onRecorded={(file) => {
            if (fileList.length >= 5) {
              message.error("Up to 5 attachments per activity");
              return;
            }
            setFileList((prev) => [
              ...prev,
              {
                uid: `voice-${Date.now()}`,
                name: file.name,
                status: "done",
                originFileObj: file,
              },
            ]);
          }}
        />
      </div>

      {/* BUTTON */}

      <Button
        type="primary"
        block
        size={isMobile ? "middle" : "large"}
        style={{ marginTop: 10 }}
        onClick={save}
      >
        Send
      </Button>

    </Modal>
  );
};

export default LeadActivityModal;